import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLocation,
  useNavigationType,
  NavigationType,
} from 'react-router-dom';

type AppHistoryContextType = {
  previousRoute: string | null;
  currentRoute: string;
};

const AppHistoryContext = createContext<AppHistoryContextType | undefined>(
  undefined,
);

export const AppHistoryProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();
  const navigationType = useNavigationType();

  const [previousRoute, setPreviousRoute] = useState<string | null>(null);

  const currentLocationRef = useRef({
    pathname: location.pathname,
    search: location.search,
  });

  const getCurrentRoute = useCallback(() => {
    const currentLocation = currentLocationRef.current;
    return currentLocation.pathname + currentLocation.search;
  }, []);

  useEffect(() => {
    if (
      navigationType !== NavigationType.Replace &&
      currentLocationRef.current.pathname !== location.pathname
    ) {
      setPreviousRoute(getCurrentRoute());
    }
    currentLocationRef.current = {
      pathname: location.pathname,
      search: location.search,
    };
  }, [location.pathname, location.search, navigationType, getCurrentRoute]);

  const value: AppHistoryContextType = useMemo(
    () => ({
      previousRoute,
      currentRoute: getCurrentRoute(),
    }),
    [previousRoute, getCurrentRoute],
  );

  return (
    <AppHistoryContext.Provider value={value}>
      {children}
    </AppHistoryContext.Provider>
  );
};

export const useAppHistoryContext = () => {
  const context = useContext(AppHistoryContext);
  if (!context) {
    throw new Error(
      'useAppHistoryContext must be used within an AppHistoryProvider',
    );
  }
  return context;
};
